// Toolbar.tsx — session toolbar with recording controls + view toggle

import React, { useRef, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Mic, Square, FileAudio, RotateCcw, StopCircle, Drama, MonitorSpeaker, TriangleAlert } from "lucide-react";
import { useAuth } from "../../auth/AuthProvider";
import { API_BASE } from "../../config";
import { Button } from "../../components/ui/Button";
import { useToast } from "../../components/Toast";
import { useConsents } from "../../hooks/useConsents";
import { sendWithRetry } from "./lib/sendWithRetry";
import {
  acquireOnlineCallAudio,
  isOnlineCaptureSupported,
  NoDisplayAudioError,
  MicrophoneDeniedError,
} from "./lib/onlineAudioCapture";
import {
  recordingStarted,
  recordingStopped,
  recordingChunkUploaded,
  recordingChunkLost,
} from "./lib/recordingStatus";

interface ToolbarProps {
  recording: boolean;
  /** Called when recording starts. If sessionId is provided, it was auto-created. */
  onStartRecording: (sessionId?: string) => void;
  onStopRecording: () => void;
  onUploadVoice: (file: File) => void;
  onExportCsv: () => void;

  sessionId?: string;
  therapistId?: string;
  patientId?: string;

  /** Called before recording starts if sessionId is missing. Should create the session and return the new sessionId. */
  onEnsureSession?: () => Promise<string>;

  // Date picker
  sessionDate?: string;
  onDateChange?: (date: string) => void;

  // Replay
  canReplay?: boolean;
  onStartReplay?: () => void;
  isReplaying?: boolean;

  // Notes/Transcript toggle
  showTranscript?: boolean;
  onToggleTranscript?: () => void;

  // Experiential tools
  onOpenExperiential?: () => void;
}

const CHUNK_MS = 30_000;
const MIN_PART_BYTES = 1024; // drop tiny garbage parts

export function Toolbar({
  recording,
  onStartRecording,
  onStopRecording,
  onUploadVoice,
  onExportCsv,

  sessionId,
  therapistId,
  patientId,

  onEnsureSession,

  sessionDate,
  onDateChange,

  canReplay,
  onStartReplay,
  isReplaying,

  showTranscript,
  onToggleTranscript,

  onOpenExperiential,
}: ToolbarProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const voiceInputRef = useRef<HTMLInputElement | null>(null);

  // GDPR: consent state for the recording warning indicator (non-blocking —
  // enforcement rollout is log-only first, so we warn but never disable).
  const {
    data: consentData,
    error: consentError,
  } = useConsents(patientId || undefined);

  const { ensureToken } = useAuth();
  const ensureTokenRef = useRef(ensureToken);
  useEffect(() => {
    ensureTokenRef.current = ensureToken;
  }, [ensureToken]);

  const getTokenOrThrow = useCallback(async (): Promise<string> => {
    const t = await ensureTokenRef.current();
    if (!t) throw new Error("No auth token – user not authenticated.");
    return t;
  }, []);

  const mediaStreamRef = useRef<MediaStream | null>(null);
  const onlineCleanupRef = useRef<(() => void) | null>(null);
  const onlineSupported = isOnlineCaptureSupported();
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaPartsRef = useRef<Blob[]>([]);

  const chunkIndexRef = useRef(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const segmentTimerRef = useRef<number | null>(null);
  const shouldContinueRef = useRef(false);
  const startingRef = useRef(false);
  const wakeLockRef = useRef<any>(null);

  const acquireWakeLock = async () => {
    if ("wakeLock" in navigator) {
      try {
        wakeLockRef.current = await (navigator as any).wakeLock.request("screen");
        console.log("[Toolbar] Wake Lock acquired");
      } catch (e) {
        console.warn("[Toolbar] Wake Lock request failed:", e);
      }
    }
  };

  const releaseWakeLock = () => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
      console.log("[Toolbar] Wake Lock released");
    }
  };

  const handleVisibilityChange = async () => {
    if (document.visibilityState === "visible" && shouldContinueRef.current) {
      await acquireWakeLock();
    }
  };

  // Store sessionId in ref for immediate access after onEnsureSession (props update async)
  const resolvedSessionIdRef = useRef<string | undefined>(sessionId);

  // Keep ref in sync with prop
  useEffect(() => {
    resolvedSessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    console.log("[Toolbar] render, recording =", recording);
  }, [recording]);

  const clearTimer = () => {
    if (segmentTimerRef.current) {
      window.clearTimeout(segmentTimerRef.current);
      segmentTimerRef.current = null;
    }
  };

  const stopTracks = () => {
    if (mediaStreamRef.current) {
      console.log("[Toolbar] stopping media tracks");
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
  };

  const stopRecorderIfAny = () => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state === "recording") {
      console.log("[Toolbar] stopping recorder");
      try {
        rec.stop();
      } catch (e) {
        console.error("[Toolbar] rec.stop() failed", e);
      }
    }
  };

  const stopLocalMedia = () => {
    console.log("[Toolbar] stopLocalMedia()");
    clearTimer();
    shouldContinueRef.current = false;
    stopRecorderIfAny();
    stopTracks();
    mediaRecorderRef.current = null;
  };

  const sendChunk = async (blob: Blob, durationMs: number) => {
    chunkIndexRef.current += 1;
    const idx = chunkIndexRef.current;

    // Use ref for sessionId (updated immediately by handleStartClick, props update async)
    const sid = resolvedSessionIdRef.current;

    console.log("[Toolbar] sendChunk() called", {
      chunkIndex: idx,
      size: blob.size,
      type: blob.type,
      durationMs,
      sessionId: sid,
    });

    if (!API_BASE) throw new Error("API_BASE is not configured");
    if (!sid) throw new Error("Missing sessionId");
    if (!therapistId) throw new Error("Missing therapistId");
    if (!patientId) throw new Error("Missing patientId");
    if (!blob || blob.size === 0) throw new Error("Refusing to send empty blob");

    const token = await getTokenOrThrow();
    const url = `${API_BASE}/sessions/chunks`;

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      "Content-Type": blob.type || "audio/webm",
      "X-Session-Id": sid,
      "X-Therapist-Id": therapistId,
      "X-Patient-Id": patientId,
      "X-Chunk-Index": String(idx),
      "X-Duration-Ms": String(durationMs),
      "X-Chunk-Sent-At": String(Date.now()),
    };

    // NOTE: never log `headers` — it contains the Authorization bearer token.
    console.log("[Toolbar] >>> POST", url, {
      bodyType: "Blob",
      bodySize: blob.size,
    });

    const resp = await fetch(url, { method: "POST", headers, body: blob });

    const text = await resp.text();
    let parsed: any = text;
    try {
      parsed = text ? JSON.parse(text) : {};
    } catch {}

    console.log("[Toolbar] <<< /sessions/chunks response", {
      status: resp.status,
      ok: resp.ok,
      body: parsed,
    });

    if (!resp.ok) {
      throw new Error(typeof parsed === "object" ? JSON.stringify(parsed) : String(parsed || resp.status));
    }

    recordingChunkUploaded();
  };

  const pickMimeType = (): string | undefined => {
    const preferred = ["audio/webm;codecs=opus", "audio/webm"].find((t) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const MR: any = MediaRecorder;
      return typeof MR?.isTypeSupported === "function" ? MR.isTypeSupported(t) : true;
    });
    return preferred;
  };

  const startSegment = async () => {
    if (startingRef.current) return;
    startingRef.current = true;

    try {
      const stream = mediaStreamRef.current;
      if (!stream) throw new Error("No media stream");

      mediaPartsRef.current = [];

      const mimeType = pickMimeType();
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = rec;

      const startedAt = Date.now();

      rec.ondataavailable = (ev: BlobEvent) => {
        const b = ev.data;
        console.log("[Toolbar] ondataavailable", { size: b?.size, type: b?.type });
        if (b && b.size > 0) mediaPartsRef.current.push(b);
      };

      rec.onerror = (ev: any) => {
        console.error("[Toolbar] MediaRecorder error", ev);
      };

      rec.onstop = async () => {
        clearTimer();

        const endedAt = Date.now();
        const durationMs = Math.max(0, endedAt - startedAt);

        const parts = (mediaPartsRef.current || []).filter((p) => (p?.size || 0) >= MIN_PART_BYTES);
        mediaPartsRef.current = [];

        console.log("[Toolbar] onstop, parts(after filter):", parts.length);

        const outBlob = new Blob(parts, { type: rec.mimeType || "audio/webm" });

        if (audioUrl) {
          try {
            URL.revokeObjectURL(audioUrl);
          } catch {}
        }
        setAudioUrl(URL.createObjectURL(outBlob));

        // CRITICAL: start the next segment BEFORE awaiting the upload. Otherwise the
        // microphone stays silent for the entire upload duration (5-15s on slow networks),
        // and the recorded session is missing ~60% of the actual audio. The upload of
        // the previous chunk runs in parallel below.
        if (shouldContinueRef.current) {
          void (async () => {
            try {
              await startSegment();
            } catch (e) {
              // Capture has effectively died mid-session — tell the therapist and sync the
              // UI to reality instead of silently showing "recording" while capturing nothing.
              console.error("[Toolbar] failed to start next segment", e);
              toast.error(t("session.recordingInterrupted"));
              handleStopClick();
            }
          })();
        }

        if (outBlob.size > 0) {
          const sent = await sendWithRetry(
            () => sendChunk(outBlob, durationMs),
            3,
            (attempt, e) => console.error(`[Toolbar] sendChunk failed (attempt ${attempt}/3):`, e),
          );
          if (!sent) {
            console.error("[Toolbar] sendChunk PERMANENTLY FAILED after 3 attempts — audio chunk lost");
            recordingChunkLost();
            toast.error(t("session.chunkUploadFailed"));
          }
        } else {
          console.error("[Toolbar] Segment blob empty after filtering; not sending.");
        }
      };

      console.log("[Toolbar] recorder.start() (no timeslice)");
      rec.start();

      segmentTimerRef.current = window.setTimeout(() => {
        const r = mediaRecorderRef.current;
        if (!r) return;
        if (r.state !== "recording") {
          console.warn("[Toolbar] segment timer fired but recorder not recording:", r.state);
          return;
        }
        console.log("[Toolbar] segment timer -> stop()");
        try {
          r.stop();
        } catch (e) {
          console.error("[Toolbar] stop() failed", e);
        }
      }, CHUNK_MS);
    } finally {
      startingRef.current = false;
    }
  };

  const handleStartClick = async () => {
    console.log("[Toolbar] Start Recording button clicked");
    chunkIndexRef.current = 0;
    shouldContinueRef.current = true;

    let started = false;
    try {
      // Ensure session exists before recording (auto-create if needed)
      if (!sessionId && onEnsureSession) {
        console.log("[Toolbar] No sessionId, calling onEnsureSession…");
        const newSid = await onEnsureSession();
        console.log("[Toolbar] onEnsureSession returned:", newSid);
        if (!newSid) {
          throw new Error("Failed to create session before recording.");
        }
        // Store in ref immediately (props won't update until next render)
        resolvedSessionIdRef.current = newSid;
      }

      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("getUserMedia not supported in this browser.");
      }

      console.log("[Toolbar] requesting mic permission…");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log("[Toolbar] mic permission granted, tracks:", stream.getTracks());
      mediaStreamRef.current = stream;

      await acquireWakeLock();
      document.addEventListener("visibilitychange", handleVisibilityChange);

      await startSegment();
      started = true;
    } catch (err) {
      console.error("[Toolbar] start error", err);
      shouldContinueRef.current = false;
      stopLocalMedia();

      const name = (err as DOMException)?.name;
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        toast.error(t("session.micDenied"));
      } else if (name === "NotFoundError" || name === "NotReadableError") {
        toast.error(t("session.micUnavailable"));
      } else {
        toast.error(t("session.recordStartFailed"));
      }
    }

    // Only notify parent that recording started if setup actually succeeded.
    // Otherwise the UI would show "recording" while nothing is being captured.
    if (started) {
      if (mediaStreamRef.current) recordingStarted(mediaStreamRef.current);
      try {
        // Pass the resolved sessionId (might be newly created)
        onStartRecording(resolvedSessionIdRef.current);
      } catch (e) {
        console.error("[Toolbar] parent onStartRecording error", e);
      }
    }
  };

  const handleStopClick = () => {
    console.log("[Toolbar] Stop Recording button clicked");
    shouldContinueRef.current = false;
    clearTimer();
    stopRecorderIfAny();
    stopTracks();
    onlineCleanupRef.current?.();
    onlineCleanupRef.current = null;
    releaseWakeLock();
    recordingStopped();
    document.removeEventListener("visibilitychange", handleVisibilityChange);

    try {
      onStopRecording();
    } catch (e) {
      console.error("[Toolbar] parent onStopRecording error", e);
    }
  };

  const handleStartOnlineClick = async () => {
    console.log("[Toolbar] Start Online Recording button clicked");
    chunkIndexRef.current = 0;
    shouldContinueRef.current = true;

    let started = false;
    try {
      // Ensure session exists before recording (auto-create if needed)
      if (!sessionId && onEnsureSession) {
        console.log("[Toolbar] No sessionId, calling onEnsureSession…");
        const newSid = await onEnsureSession();
        console.log("[Toolbar] onEnsureSession returned:", newSid);
        if (!newSid) {
          throw new Error("Failed to create session before recording.");
        }
        resolvedSessionIdRef.current = newSid;
      }

      const { stream, cleanup } = await acquireOnlineCallAudio(() => {
        // Browser "Stop sharing" bar or shared tab closed → same as pressing stop.
        console.log("[Toolbar] display audio ended → stopping recording");
        handleStopClick();
      });
      onlineCleanupRef.current = cleanup;
      mediaStreamRef.current = stream;

      await acquireWakeLock();
      document.addEventListener("visibilitychange", handleVisibilityChange);

      await startSegment();
      started = true;
    } catch (err) {
      console.error("[Toolbar] online start error", err);
      shouldContinueRef.current = false;
      stopLocalMedia();
      onlineCleanupRef.current?.();
      onlineCleanupRef.current = null;

      if (err instanceof NoDisplayAudioError) {
        toast.error(t("session.onlineNoAudioTrack"));
      } else if (err instanceof MicrophoneDeniedError) {
        toast.error(t("session.onlineMicDenied"));
      } else if ((err as DOMException)?.name === "NotAllowedError") {
        // Share picker cancelled — deliberate user action, not an error.
        console.log("[Toolbar] share picker cancelled by user");
      } else {
        toast.error(t("session.onlineStartFailed"));
      }
    }

    if (started) {
      if (mediaStreamRef.current) recordingStarted(mediaStreamRef.current);
      try {
        onStartRecording(resolvedSessionIdRef.current);
      } catch (e) {
        console.error("[Toolbar] parent onStartRecording error", e);
      }
    }
  };

  const onPickVoice = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    console.log("[Toolbar] onPickVoice", { hasFile: !!f, name: f?.name, size: f?.size, type: f?.type });
    if (!f) return;
    try {
      onUploadVoice(f);
    } catch (err) {
      console.error("[Toolbar] onUploadVoice error", err);
    }
    e.currentTarget.value = "";
  };

  useEffect(() => {
    return () => {
      console.log("[Toolbar] unmount → cleaning up media");
      shouldContinueRef.current = false;
      clearTimer();
      stopLocalMedia();
      onlineCleanupRef.current?.();
      onlineCleanupRef.current = null;
      releaseWakeLock();
      recordingStopped();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (audioUrl) {
        try {
          URL.revokeObjectURL(audioUrl);
        } catch {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="spn-toolbar">
      {/* Recording controls */}
      <div className="spn-toolbar-group">
        {!recording ? (
          <>
            <Button
              variant="primary"
              title={t("session.startRecording")}
              aria-label={t("session.startRecording")}
              onClick={handleStartClick}
            >
              <Mic size={14} />
              <span>{t("session.record")}</span>
            </Button>
            {onlineSupported && (
              <Button
                variant="primary"
                title={t("session.startOnlineRecording")}
                aria-label={t("session.startOnlineRecording")}
                onClick={handleStartOnlineClick}
              >
                <MonitorSpeaker size={14} />
                <span>{t("session.recordOnline")}</span>
              </Button>
            )}
          </>
        ) : (
          <Button
            variant="danger"
            className="ui-btn--pulse"
            title={t("session.stopRecording")}
            aria-label={t("session.stopRecording")}
            onClick={handleStopClick}
          >
            <Square size={13} />
            <span>{t("session.recording")}</span>
          </Button>
        )}

        <Button
          variant="ghost"
          iconOnly
          title={t("session.uploadVoice")}
          aria-label={t("session.uploadVoice")}
          onClick={() => voiceInputRef.current?.click()}
        >
          <FileAudio size={14} />
        </Button>

        {/* GDPR recording-consent indicator (non-blocking, log-only rollout) */}
        {patientId && consentError && (
          <span
            className="spn-consent-warn spn-consent-warn--error"
            title={consentError}
          >
            <TriangleAlert size={12} />
            <span>{t("consent.statusUnavailable")}</span>
          </span>
        )}
        {patientId && !consentError && consentData && !consentData.state.recording && (
          <span
            className="spn-consent-warn"
            title={t("consent.recordingWarningTooltip")}
          >
            <TriangleAlert size={12} />
            <span>{t("consent.recordingWarning")}</span>
          </span>
        )}
      </div>

      <div className="spn-toolbar-group">
        {canReplay && onStartReplay && (
          <Button
            variant="outline"
            aria-pressed={!!isReplaying}
            title={isReplaying ? t("session.exitReplay") : t("session.replaySession")}
            aria-label={isReplaying ? t("session.exitReplay") : t("session.replaySession")}
            onClick={onStartReplay}
          >
            {isReplaying ? <StopCircle size={14} /> : <RotateCcw size={14} />}
            <span>{t("session.replayBtn")}</span>
          </Button>
        )}
      </div>

      {patientId && onOpenExperiential && (
        <div className="spn-toolbar-group">
          <Button
            variant="ghost"
            iconOnly
            title={t("experiential.title")}
            aria-label={t("experiential.title")}
            onClick={onOpenExperiential}
          >
            <Drama size={14} />
          </Button>
        </div>
      )}

      {/* Spacer to push date to right */}
      <div style={{ flex: 1 }} />

      {/* Date picker */}
      {sessionDate !== undefined && onDateChange && (
        <input
          type="date"
          className="spn-toolbar-date"
          value={sessionDate}
          onChange={(e) => onDateChange(e.target.value)}
          aria-label={t("session.sessionDate")}
        />
      )}

      {/* Hidden file input */}
      <input ref={voiceInputRef} type="file" accept="audio/*" hidden onChange={onPickVoice} />
    </div>
  );
}

export default Toolbar;
